<script lang="ts">
  import { browser } from '$app/environment';
  import type { PriceAlert, PriceAlertInput } from '$types/races';
  import { createPriceAlert } from '$utils/alerts';
  import { Bookmaker } from '$utils/bookmakers';
  import Select from 'svelte-select';
  import Button from './Button.svelte';
  import { clearPriceAlert } from './PriceAlertsModal.svelte';

  export let priceAlertInput: PriceAlertInput;

  let bookmakerSelectElementOpen = false;
  let bookmakerSelectInputValue = '';

  // used to determine if the alert is persistent or not
  // and keep this preference cached in localStorage for subsequent alerts
  export let persistent: boolean = browser
    ? localStorage.getItem('defaultPriceAlertPersistence') === 'true' || false
    : false;

  // used for the select dropdown
  let bookmakersSelect =
    (priceAlertInput.bookmakers?.map((b) => {
      return { value: b, label: b };
    }) ||
      []) ??
    [];
  $: {
    // make sure it's always a list
    if (!bookmakersSelect) bookmakersSelect = [];
  }

  // update localstorage
  $: {
    if (browser)
      localStorage.setItem(
        'defaultPriceAlertPersistence',
        persistent.toString()
      );
  }

  let lastSelectedBookmakers: { value: string; label: string }[] = browser
    ? JSON.parse(localStorage.getItem('lastPriceAlertBookmakers') || '[]')
    : [];

  let submitted = false;

  const allBookmakers = Object.values(Bookmaker)
    .filter((b) => b !== Bookmaker.BETFAIR)
    .sort((a, b) => a.valueOf().localeCompare(b.valueOf()))
    .map((b) => b.valueOf())
    .sort((a, b) => {
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b);
    });
  // TODO: add this back in when persistence is implemented
  //   allBookmakers.unshift('Any Bookmaker');

  // get "default_trigger_type" from local storage, or default to 'Greater Than (>)'
  let internalTrigger: string = browser
    ? localStorage.getItem('defaultTriggerType') || 'Greater Than (>)'
    : 'Greater Than (>)';

  export let trigger: PriceAlert['alertType'] = 'GREATER_THAN_OR_LESS_THAN';
  $: {
    // store in localstorage
    if (browser) localStorage.setItem('defaultTriggerType', internalTrigger);
    // handle the conversion between trigger types
    if (internalTrigger === 'Less Than (<)') {
      trigger = 'LESS_THAN';
    } else if (internalTrigger === 'Greater Than (>)') {
      trigger = 'GREATER_THAN';
    } else if (internalTrigger === 'Greater Than Or Less Than (<>)') {
      trigger = 'GREATER_THAN_OR_LESS_THAN';
    }
    priceAlertInput.alertType = trigger;
  }

  const saveAlert = async () => {
    // store the last selected bookmakers
    if (browser)
      localStorage.setItem(
        'lastPriceAlertBookmakers',
        JSON.stringify(bookmakersSelect)
      );

    submitted = true;
    // pull out the runner attribute from the input
    const { runner, ...input } = priceAlertInput; // eslint-disable-line @typescript-eslint/no-unused-vars
    await createPriceAlert(input);
    submitted = false;
    clearPriceAlert();
  };

  const cancelAlert = () => {
    submitted = false;
    clearPriceAlert();
  };

  function handleClick(
    items: { value: string; label: string; index?: number }[],
    options?: { clear?: boolean }
  ) {
    // clear filter text
    bookmakerSelectInputValue = '';

    if (options?.clear) {
      bookmakersSelect = [];
      priceAlertInput.bookmakers = [];
      priceAlertInput.priceIds = [];
      bookmakerSelectElementOpen = false;
    }

    // iterate over items
    for (const i in items) {
      if (Object.prototype.hasOwnProperty.call(items, i)) {
        const item = items[i];
        // find the price id associated with the runner and bookmaker and price type
        const market = priceAlertInput.runner?.bookmakerMarkets?.find((bm) => {
          if (bm.bookmaker === item.value) {
            return bm;
          }
        });
        if (market) {
          priceAlertInput.priceIds = [...priceAlertInput.priceIds, market.id];
        }

        priceAlertInput.bookmakers = [
          ...priceAlertInput.bookmakers,
          item.value
        ];
        bookmakersSelect = [...bookmakersSelect, item];
      }
    }
  }
</script>

<div
  class="mb-1 flex h-fit min-w-0 flex-row items-center space-x-4 rounded border-2 border-sky-100 bg-sky-50 p-5 text-xs text-gray-800"
>
  <div class="w-full flex-col">
    <div class="flex flex-row items-center">
      <span class="flex-1 text-xs font-semibold md:text-sm"
        >New Price Alert for {priceAlertInput?.runner?.number}. {priceAlertInput
          ?.runner?.name}</span
      >
      <span
        class="flex-0 inline-flex items-center rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-800"
        >{priceAlertInput.priceType === 'FIXED_WIN'
          ? 'Fixed Win'
          : 'Fixed Place'}</span
      >
    </div>
    <div
      class="bg-background relative mt-1 flex w-36 rounded-md shadow-sm md:w-full"
    >
      <span
        class="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500"
        >Bookmakers</span
      >

      <Select
        class="flex"
        items={allBookmakers}
        required
        multiple
        clearable={false}
        multiFullItemClearable
        bind:value={bookmakersSelect}
        bind:listOpen={bookmakerSelectElementOpen}
        bind:filterText={bookmakerSelectInputValue}
      >
        <div slot="clear-icon" />
        <div slot="list" let:filteredItems class="flex flex-col text-xs">
          {#if lastSelectedBookmakers.length}
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
              on:click={() =>
                handleClick(lastSelectedBookmakers, { clear: true })}
              on:keypress={() =>
                handleClick(lastSelectedBookmakers, { clear: true })}
              class="flex flex-row items-center p-2 hover:cursor-pointer hover:bg-slate-100"
            >
              <div class="bookmaker-any-bookmaker mr-1 h-3 w-3 rounded-full" />
              Last Selected Bookmakers ({lastSelectedBookmakers.length})
            </div>
          {/if}
          {#each filteredItems as item (item)}
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
              on:click={() => handleClick([item])}
              on:keypress={() => handleClick([item])}
              class="flex flex-row items-center p-2 hover:cursor-pointer hover:bg-slate-100"
            >
              <div
                class="bookmaker-{item.label
                  .toLowerCase()
                  .replace(' ', '')} mr-1 h-3 w-3 rounded-full"
              />
              {item.label}
            </div>
          {/each}
        </div>
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          slot="selection"
          let:selection
          class="flex flex-row items-center pt-1 text-xs hover:cursor-pointer"
          on:click={() => handleClick([selection])}
          on:keypress={() => handleClick([selection])}
        >
          <div class="flex flex-row items-center">
            <div
              class="bookmaker-{selection.label
                .toLowerCase()
                .replace(' ', '')} mr-1 h-3 w-3 rounded-full"
            />
            {selection.label}
          </div>
        </div>
        <!-- <div slot="clear-icon" class="text-xs">🔥</div> -->
        <!-- {#if bookmaker !== 'ANY BOOKMAKER'}
                  <option>ANY BOOKMAKER</option>
                {/if} -->
        <!-- <option>{bookmaker}</option>
                {#each allBookmakers as bookie}
                  {#if bookie !== bookmaker}
                    <option>{bookie}</option>
                  {/if}
                {/each} -->
      </Select>
    </div>
    <div class="relative mt-1 flex rounded-md shadow-sm">
      <span
        class="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500"
        >Trigger</span
      >
      <select
        bind:value={internalTrigger}
        id="trigger"
        name="trigger"
        class="relative block w-full rounded-r-md border-gray-300 text-xs focus:z-10 focus:border-blue-500 focus:ring-blue-500"
      >
        <option>{'Greater Than Or Less Than (<>)'}</option>
        <option>{'Less Than (<)'}</option>
        <option>{'Greater Than (>)'}</option>
      </select>
    </div>
    <div class="relative mt-1 flex rounded-md shadow-sm">
      <span
        class="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500"
        >Price</span
      >
      <input
        bind:value={priceAlertInput.value}
        type="text"
        name="price"
        id="price"
        class="relative block w-full rounded-r-md border-gray-300 text-xs focus:z-10 focus:border-blue-500 focus:ring-blue-500"
      />
    </div>
    <!-- <div class="relative mt-1 flex rounded-md shadow-sm">
          <span
            class="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 "
            >Persistent</span
          >
          <input
            bind:checked={persistent}
            type="checkbox"
            name="persistent"
            id="persistent"
            autocomplete="persistent"
            class="relative block w-full rounded-r-md border-gray-300 text-xs focus:z-10 focus:border-blue-500 focus:ring-blue-500"
          />
        </div> -->
    <!-- <div class="relative mt-1 flex rounded-md shadow-sm">
            <span
              class="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm"
              >±</span
            >
            <input
              type="text"
              name="price"
              id="price"
              autocomplete="price"
              class="relative block w-full rounded-r-md border-gray-300 focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              value={0}
            />
          </div> -->
    <div class="pt-1">
      <div class="flex justify-end gap-x-2">
        <Button on:click={cancelAlert} size="small" color="white">Cancel</Button
        >
        <Button
          disabled={submitted}
          on:click={saveAlert}
          type="submit"
          size="small"
        >
          Save
        </Button>
      </div>
    </div>
  </div>
</div>
